/**
 * Custom token store for the Sharetribe SDK.
 *
 * This wrapper around the default browser cookie store adds validation
 * to ensure that the token returned has the required structure.
 *
 * The issue this solves:
 * Safari's stricter cookie handling can cause the js-cookie library's
 * getJSON() to fail silently on JSON parsing, returning the raw string
 * instead of null. When the SDK receives this string (which is truthy),
 * it tries to access token.token_type which is undefined for strings,
 * resulting in "Unknown token type: undefined" error.
 *
 * The fix:
 * Validate that the token is an object with the required token_type property
 * before returning it. If invalid, return null to allow the SDK to handle
 * it as an unauthenticated request.
 */

/**
 * Validates that the token has the required structure
 * @param {any} token - The token to validate
 * @returns {boolean} - Whether the token is valid
 */
const isValidToken = token => {
  // Token must be an object (not null, not a string, not undefined)
  if (typeof token !== 'object' || token === null) {
    return false;
  }

  // Token must have token_type and access_token properties
  if (!token.token_type || !token.access_token) {
    return false;
  }

  return true;
};

/**
 * Creates a custom token store that wraps the default browser cookie store
 * with validation to prevent Safari-related token parsing issues.
 *
 * @param {Object} baseStore - The base token store to wrap (from SDK)
 * @returns {Object} - A token store with getToken, setToken, and removeToken methods
 */
export const createValidatingTokenStore = baseStore => {
  return {
    getToken: () => {
      const token = baseStore.getToken();

      // Validate the token before returning
      if (token && !isValidToken(token)) {
        // Token is malformed (likely a string due to Safari JSON parse issue)
        // Return null to let the SDK treat this as no token
        if (process.env.NODE_ENV === 'development') {
          console.warn(
            'Invalid token structure detected in cookie store. ' +
              'This may be due to browser cookie handling issues. ' +
              'Returning null to allow proper authentication flow.'
          );
        }
        return null;
      }

      return token;
    },
    setToken: token => {
      return baseStore.setToken(token);
    },
    removeToken: () => {
      return baseStore.removeToken();
    },
  };
};
